import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { getLocale, saveLocale, deleteLocale } from '../../services/CommonServices';
import { useLanguage } from '../../contexts/LanguageContext';
import { useInstance } from '../../contexts/InstanceContext';

// 로컬 기본 메시지 import
import koMessages from '../../locale/ko.json';
import enMessages from '../../locale/en.json';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 0.2s ease-in-out;
  z-index: 10000;
`;

const Modal = styled.div`
  width: 95%;
  max-width: 1000px;
  max-height: 90vh;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f9fafb;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: #e5e7eb;
    color: #111827;
  }
`;

const Toolbar = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background-color: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #059669;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  background-color: #f3f4f6;
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
`;

const Tr = styled.tr`
  &:hover {
    background-color: #f9fafb;
  }

  ${props => props.missing && `
    background-color: #fef3c7;
    &:hover {
      background-color: #fde68a;
    }
  `}
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  color: #374151;
  vertical-align: top;
`;

const KeyCell = styled(Td)`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #1f2937;
  max-width: 250px;
  word-break: break-all;
`;

const ValueCell = styled(Td)`
  max-width: 300px;

  ${props => props.empty && `
    color: #9ca3af;
    font-style: italic;
  `}
`;

const ActionCell = styled(Td)`
  width: 100px;
  text-align: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 4px;

  &:hover {
    background-color: #e5e7eb;
    color: #111827;
  }

  ${props => props.danger && `
    &:hover {
      background-color: #fee2e2;
      color: #dc2626;
    }
  `}
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;

  ${props => props.type === 'missing' && `
    background-color: #fef3c7;
    color: #92400e;
  `}

  ${props => props.type === 'db' && `
    background-color: #dbeafe;
    color: #1e40af;
  `}

  ${props => props.type === 'local' && `
    background-color: #d1fae5;
    color: #065f46;
  `}
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f9fafb;
`;

const Stats = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

// 편집 모달
const EditOverlay = styled(Overlay)`
  z-index: 10001;
`;

const EditModal = styled.div`
  width: 90%;
  max-width: 500px;
  background-color: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const EditTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
`;

const InputGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background-color: #f3f4f6;
    color: #6b7280;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  ${props => props.primary ? `
    background-color: #3b82f6;
    color: white;
    border: none;

    &:hover {
      background-color: #2563eb;
    }

    &:disabled {
      background-color: #93c5fd;
      cursor: not-allowed;
    }
  ` : `
    background-color: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover {
      background-color: #e5e7eb;
    }
  `}
`;

function TranslationManager({ isOpen, onClose }) {
  const { locale } = useLanguage();
  const { memberId: member_id } = useInstance();
  const [translations, setTranslations] = useState([]);
  const [dbTranslations, setDbTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');

  // 편집 상태
  const [editItem, setEditItem] = useState(null);
  const [editKo, setEditKo] = useState('');
  const [editEn, setEditEn] = useState('');
  const [saving, setSaving] = useState(false);

  // 새 키 추가 상태
  const [isAdding, setIsAdding] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newKo, setNewKo] = useState('');
  const [newEn, setNewEn] = useState('');

  // 번역 데이터 로드
  useEffect(() => {
    if (!isOpen || !member_id) return;

    const loadTranslations = async () => {
      setLoading(true);

      try {
        // API에서 DB 번역 가져오기
        const res = await getLocale(member_id);
        if (res.data.success && res.data.messages) {
          setDbTranslations(res.data.messages);
        }
      } catch (err) {
        console.error('Failed to load translations from API:', err);
      }

      setLoading(false);
    };

    loadTranslations();
  }, [isOpen, member_id]);

  // 로컬 + DB 번역 병합하여 목록 생성
  const translationList = useMemo(() => {
    const allKeys = new Set([
      ...Object.keys(koMessages),
      ...Object.keys(enMessages),
      ...Object.keys(dbTranslations.ko || {}),
      ...Object.keys(dbTranslations.en || {}),
    ]);

    return Array.from(allKeys).map(key => {
      const localKo = koMessages[key];
      const localEn = enMessages[key];
      const dbKo = dbTranslations.ko?.[key];
      const dbEn = dbTranslations.en?.[key];

      // 최종 값 (DB가 우선)
      const ko = dbKo ?? localKo;
      const en = dbEn ?? localEn;

      // 상태 판단
      const isMissing = !ko || !en;
      const isFromDb = dbKo !== undefined || dbEn !== undefined;
      const isLocalOnly = !isFromDb && (localKo !== undefined || localEn !== undefined);

      return {
        key,
        ko: ko || '',
        en: en || '',
        localKo,
        localEn,
        dbKo,
        dbEn,
        isMissing,
        isFromDb,
        isLocalOnly,
      };
    }).sort((a, b) => a.key.localeCompare(b.key));
  }, [dbTranslations]);

  // 필터링된 목록
  const filteredList = useMemo(() => {
    let list = translationList;

    // 검색 필터
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      list = list.filter(item =>
        item.key.toLowerCase().includes(query) ||
        item.ko.toLowerCase().includes(query) ||
        item.en.toLowerCase().includes(query)
      );
    }

    // 상태 필터
    if (filter === 'missing') {
      list = list.filter(item => item.isMissing);
    } else if (filter === 'db') {
      list = list.filter(item => item.isFromDb);
    } else if (filter === 'local') {
      list = list.filter(item => item.isLocalOnly);
    }

    return list;
  }, [translationList, searchQuery, filter]);

  // 편집 시작
  const handleEdit = (item) => {
    setEditItem(item);
    setEditKo(item.ko);
    setEditEn(item.en);
  };

  // 편집 저장
  const handleSave = async () => {
    if (!editItem) return;

    setSaving(true);
    try {
      await saveLocale(member_id, editItem.key, { ko: editKo, en: editEn });

      // DB 번역 업데이트
      setDbTranslations(prev => ({
        ko: { ...prev.ko, [editItem.key]: editKo },
        en: { ...prev.en, [editItem.key]: editEn },
      }));

      setEditItem(null);
      alert('저장되었습니다!');
    } catch (err) {
      console.error('Save failed:', err);
      alert('저장에 실패했습니다.');
    }
    setSaving(false);
  };

  // 새 키 추가
  const handleAdd = async () => {
    if (!newKey.trim()) {
      alert('키를 입력해주세요.');
      return;
    }

    if (translationList.find(item => item.key === newKey)) {
      alert('이미 존재하는 키입니다.');
      return;
    }

    setSaving(true);
    try {
      await saveLocale(member_id, newKey, { ko: newKo, en: newEn });

      // DB 번역 업데이트
      setDbTranslations(prev => ({
        ko: { ...prev.ko, [newKey]: newKo },
        en: { ...prev.en, [newKey]: newEn },
      }));

      setIsAdding(false);
      setNewKey('');
      setNewKo('');
      setNewEn('');
      alert('추가되었습니다!');
    } catch (err) {
      console.error('Add failed:', err);
      alert('추가에 실패했습니다.');
    }
    setSaving(false);
  };

  // 삭제 (DB에서만 삭제, 로컬은 유지)
  const handleDelete = async (key) => {
    if (!window.confirm(`"${key}" 키를 삭제하시겠습니까?\n(로컬 기본값이 있으면 유지됩니다)`)) {
      return;
    }

    try {
      // 백엔드 DELETE API 호출
      const res = await deleteLocale(member_id, key);

      if (res.data.success) {
        // 성공 시 로컬 상태에서도 제거
        setDbTranslations(prev => {
          const newKo = { ...prev.ko };
          const newEn = { ...prev.en };
          delete newKo[key];
          delete newEn[key];
          return { ko: newKo, en: newEn };
        });
        alert('삭제되었습니다.');
      } else {
        alert('삭제 실패: ' + res.data.message);
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  if (!isOpen) return null;

  const missingCount = translationList.filter(t => t.isMissing).length;
  const dbCount = translationList.filter(t => t.isFromDb).length;

  return (
    <>
      <Overlay onClick={onClose}>
        <Modal onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <Title>
              <span>🌐</span>
              번역 관리
            </Title>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </ModalHeader>

          <Toolbar>
            <SearchInput
              type="text"
              placeholder="키 또는 텍스트 검색..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <FilterSelect value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="all">전체 ({translationList.length})</option>
              <option value="missing">누락 ({missingCount})</option>
              <option value="db">DB 저장 ({dbCount})</option>
              <option value="local">로컬만 ({translationList.length - dbCount})</option>
            </FilterSelect>
            <AddButton onClick={() => setIsAdding(true)}>
              + 새 키 추가
            </AddButton>
          </Toolbar>

          <TableContainer>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                로딩 중...
              </div>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th style={{ width: '30%' }}>키</Th>
                    <Th style={{ width: '30%' }}>한국어</Th>
                    <Th style={{ width: '30%' }}>영어</Th>
                    <Th style={{ width: '10%' }}>작업</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map(item => (
                    <Tr key={item.key} missing={item.isMissing}>
                      <KeyCell>
                        {item.key}
                        {item.isMissing && <Badge type="missing">누락</Badge>}
                        {item.isFromDb && <Badge type="db">DB</Badge>}
                      </KeyCell>
                      <ValueCell empty={!item.ko}>
                        {item.ko || '(없음)'}
                      </ValueCell>
                      <ValueCell empty={!item.en}>
                        {item.en || '(없음)'}
                      </ValueCell>
                      <ActionCell>
                        <IconButton onClick={() => handleEdit(item)} title="편집">
                          ✏️
                        </IconButton>
                        {item.isFromDb && (
                          <IconButton danger onClick={() => handleDelete(item.key)} title="삭제">
                            🗑️
                          </IconButton>
                        )}
                      </ActionCell>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>

          <ModalFooter>
            <Stats>
              총 {translationList.length}개 키 |
              누락 {missingCount}개 |
              DB 저장 {dbCount}개
            </Stats>
            <Button onClick={onClose}>닫기</Button>
          </ModalFooter>
        </Modal>
      </Overlay>

      {/* 편집 모달 */}
      {editItem && (
        <EditOverlay onClick={() => setEditItem(null)}>
          <EditModal onClick={e => e.stopPropagation()}>
            <EditTitle>번역 편집</EditTitle>

            <InputGroup>
              <Label>키</Label>
              <Input value={editItem.key} disabled />
            </InputGroup>

            <InputGroup>
              <Label>한국어 (KO)</Label>
              <TextArea
                value={editKo}
                onChange={e => setEditKo(e.target.value)}
                placeholder="한국어 텍스트 입력..."
              />
            </InputGroup>

            <InputGroup>
              <Label>영어 (EN)</Label>
              <TextArea
                value={editEn}
                onChange={e => setEditEn(e.target.value)}
                placeholder="Enter English text..."
              />
            </InputGroup>

            <ButtonGroup>
              <Button onClick={() => setEditItem(null)}>취소</Button>
              <Button primary onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </Button>
            </ButtonGroup>
          </EditModal>
        </EditOverlay>
      )}

      {/* 새 키 추가 모달 */}
      {isAdding && (
        <EditOverlay onClick={() => setIsAdding(false)}>
          <EditModal onClick={e => e.stopPropagation()}>
            <EditTitle>새 번역 키 추가</EditTitle>

            <InputGroup>
              <Label>키 *</Label>
              <Input
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                placeholder="예: header.newTitle"
              />
            </InputGroup>

            <InputGroup>
              <Label>한국어 (KO)</Label>
              <TextArea
                value={newKo}
                onChange={e => setNewKo(e.target.value)}
                placeholder="한국어 텍스트 입력..."
              />
            </InputGroup>

            <InputGroup>
              <Label>영어 (EN)</Label>
              <TextArea
                value={newEn}
                onChange={e => setNewEn(e.target.value)}
                placeholder="Enter English text..."
              />
            </InputGroup>

            <ButtonGroup>
              <Button onClick={() => setIsAdding(false)}>취소</Button>
              <Button primary onClick={handleAdd} disabled={saving}>
                {saving ? '추가 중...' : '추가'}
              </Button>
            </ButtonGroup>
          </EditModal>
        </EditOverlay>
      )}
    </>
  );
}

TranslationManager.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TranslationManager;
